import React, { useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, X, Loader2 } from 'lucide-react';
import { resizeImage } from '../lib/resizeImage';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX = 5;
const MAX_MB = 10;

export const PhotoPicker = ({ photos, setPhotos, busy, setBusy }) => {
  const inputRef = useRef(null);

  const uploadOne = async (original) => {
    setBusy((b) => b + 1);
    try {
      const file = await resizeImage(original);
      if (file.size > MAX_MB * 1024 * 1024) {
        toast.error(`${original.name} is too large`, { description: 'Please take a new photo or send a screenshot of it instead.' });
        return;
      }
      const body = new FormData();
      body.append('file', file);
      const { data } = await axios.post(`${API}/api/estimate-photos`, body);
      setPhotos((p) => [...p, { id: data.id, preview: URL.createObjectURL(file), name: original.name }]);
    } catch (err) {
      toast.error(`Couldn't upload ${original.name}`, { description: err.response?.data?.detail || 'Please try another photo.' });
    } finally {
      setBusy((b) => b - 1);
    }
  };

  const onPick = (e) => {
    const room = MAX - photos.length - busy;
    const files = Array.from(e.target.files || []);
    if (files.length > room) toast.error(`You can attach up to ${MAX} photos`);
    files.slice(0, Math.max(room, 0)).forEach(uploadOne);
    e.target.value = '';
  };

  const remove = (id) => setPhotos((p) => {
    const gone = p.find((x) => x.id === id);
    if (gone) URL.revokeObjectURL(gone.preview);
    return p.filter((x) => x.id !== id);
  });

  return (
    <div className="w-photos" data-testid="estimate-photos">
      <div className="w-photo-grid">
        {photos.map((p, i) => (
          <div key={p.id} className="w-photo-thumb" data-testid={`estimate-photo-${i}`}>
            <img src={p.preview} alt={p.name} />
            <button type="button" onClick={() => remove(p.id)} aria-label="Remove photo" data-testid={`estimate-photo-remove-${i}`}><X size={14} /></button>
          </div>
        ))}
        {Array.from({ length: busy }).map((_, i) => (
          <div key={`b${i}`} className="w-photo-thumb is-loading" data-testid="estimate-photo-uploading"><Loader2 size={20} className="animate-spin" /></div>
        ))}
        {photos.length + busy < MAX && (
          <button type="button" className="w-photo-add" onClick={() => inputRef.current?.click()} data-testid="estimate-photo-add">
            <Camera size={20} /><span>Add photo</span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple hidden onChange={onPick} data-testid="estimate-photo-input" />
      <span className="w-photo-hint">Optional · up to {MAX} photos of broken heads, leaks or the area · any size, we shrink them for you</span>
    </div>
  );
};

export default PhotoPicker;
